import type { ISwSettings } from "app/@types/ISwSettings";
import type { Solutions2Package } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import {
    CONTACTS_WATERFALL_PAGE_ROUTE,
    CONTACTS_WATERFALL_PAGE_ROUTE_URL,
    CRM_LIST_MAIN_PAGE_ROUTE,
    CRM_LIST_NEW_PAGE_ROUTE_URL,
    DOWNLOAD_EXTENSION_PAGE_ROUTE,
    DOWNLOAD_EXTENSION_PAGE_ROUTE_URL,
    ENRICHMENT_PAGE_ROUTE,
    ENRICHMENT_PAGE_ROUTE_URL,
    FIND_APPS_SEARCH_PAGE_ROUTE,
    HOME_PAGE_ROUTE,
    MANAGEMENT_LIST_ROUTE_URL,
    MANAGEMENT_ROUTE,
    MY_LISTS_PAGE_ROUTE,
    MY_LISTS_PAGE_ROUTE_URL,
    PREMIUM_FEATURES_PAGE_ROUTE,
    PREMIUM_FEATURES_PAGE_ROUTE_URL,
    SCORE_SETTINGS_PAGE_ROUTE,
    SCORE_SETTINGS_PAGE_ROUTE_URL,
    SI_ROOT_ROUTE,
    SI_ROOT_ROUTE_URL,
} from "app/pages/sales-intelligence/constants/routes";
import {
    MANAGEMENT_APIS_AND_WEBHOOKS_ROUTE,
    MANAGEMENT_APIS_AND_WEBHOOKS_URL,
    MANAGEMENT_CRM_INTEGRATIONS_ROUTE,
    MANAGEMENT_CRM_INTEGRATIONS_URL,
    MANAGEMENT_CRM_ROUTE,
    MANAGEMENT_CRM_URL,
    MANAGEMENT_GATEWAY,
    MANAGEMENT_GATEWAY_URL,
    MANAGEMENT_MCP,
    MANAGEMENT_MCP_URL,
    MANAGEMENT_ROOT_URL,
    SUPPRESSION_LIST_ABSTRACT_ROUTE,
} from "app/pages/sales-intelligence/constants/sl_routes";
import { ADVANCED_SEARCH_STATE_CONFIG } from "app/pages/sales-intelligence/pages/advanced-search/routing/states-config";
import { aiAgentStateConfig } from "app/pages/sales-intelligence/pages/ai-agent/routing/states-config";
import {
    COMPANY_ANALYSIS_CONFIG,
} from "app/pages/sales-intelligence/pages/company-analysis/routing/root-states-config";
import ContactsWaterfallPage
    from "app/pages/sales-intelligence/pages/contacts-waterfall/containers/ContactsWaterfallPage";
import DownloadExtensionPageContainer
    from "app/pages/sales-intelligence/pages/download-extension/containers/DownloadExtensionPageContainer/DownloadExtensionPageContainer";
import EnrichmentPageContainer
    from "app/pages/sales-intelligence/pages/enrichment/containers/EnrichmentPageContainer/EnrichmentPageContainer";
import { findAppsStateConfig } from "app/pages/sales-intelligence/pages/find-apps/routing/states-config";
import HomePageContainer from "app/pages/sales-intelligence/pages/home/HomePageContainer";
import { SalesIntelligenceContainer } from "app/pages/sales-intelligence/pages/home/SalesIntelligenceContainer";
import { SalesIntelligenceRoot } from "app/pages/sales-intelligence/pages/home/SalesIntelligenceRoot";
import CrmListsPageContainer from "app/pages/sales-intelligence/pages/lists/containers/CrmListsPageContainer";
import MyListsPageContainer from "app/pages/sales-intelligence/pages/lists/containers/MyListsPageContainer";
import APIsAndWebhooksPageContainer
    from "app/pages/sales-intelligence/pages/management/containers/APIsAndWebhooksPageContainer/APIsAndWebhooksPageContainer";
import GatewayPageContainer
    from "app/pages/sales-intelligence/pages/management/containers/GatewayPageContainer/GatewayPageContainer";
import ManageCRMsPageContainer
    from "app/pages/sales-intelligence/pages/management/containers/ManageCRMsPageContainer/ManageCRMsPageContainer";
import MCPServersPageContainer
    from "app/pages/sales-intelligence/pages/management/containers/MCPServersPageContainer/MCPServersPageContainer";
import ManagementPageContainer from "app/pages/sales-intelligence/pages/management/ManagementContainer";
import {
    crmManagementTransitionHook,
} from "app/pages/sales-intelligence/pages/management/utils/crmManagementTransitionHook";
import { resolveGatewayPageTitle } from "app/pages/sales-intelligence/pages/management/utils/resolveGatewayPageTitle";
import {
    createResolveGatewayPageTrackingId,
} from "app/pages/sales-intelligence/pages/management/utils/resolveGatewayPageTrackingId";
import {
    OPPORTUNITY_LIST_STATE_CONFIG,
} from "app/pages/sales-intelligence/pages/opportunity-list/routing/states-config";
import {
    PremiumFeaturesContainer,
} from "app/pages/sales-intelligence/pages/premium-features/containers/PremiumFeatures/PremiumFeatures";
import ScoringSettingsPageContainer
    from "app/pages/sales-intelligence/pages/scoring-settings/containers/ScoringSettingsSignalsHandlerContainer";
import { onEnterSalesIntelligenceRoot } from "app/pages/sales-intelligence/routing/onEnterSalesIntelligenceRoot";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { SecondaryBarType } from "app/reducers/_reducers/SecondaryBarReducer";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { isTouchUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import type { RouterStateConfig } from "single-spa/react-app/router/IRouterState";

import { dynamicsConfig } from "./dynamicsConfig";
import { employeeProfilesConfig } from "./employeeProfilesConfig";
import { extensionManageConfig } from "./extensionManageConfig";
import { hubspotConfig } from "./hubspotConfig";
import { playbooksConfig } from "./playbooksConfig";
import { salesforceConfig } from "./salesforceConfig";
import { sfConvertConfig } from "./sfConvertPageConfig";
import { signalsManagementConfig } from "./signalsManagementConfig";
import { suppressionListConfig } from "./suppressionListConfig";
import { teamsManageConfig } from "./teamsManageConfig";
import { homePageParams } from "app/pages/sales-intelligence/pages/home/types/routing-params";

const isTouchUserAccount = (): boolean => isTouchUser(swSettings as ISwSettings);

const salesIntelligenceMain: RouterStateConfig = {
    [SI_ROOT_ROUTE]: {
        parent: "sw",
        abstract: true,
        packageName: "salesIntelligence",
        url: SI_ROOT_ROUTE_URL,
        component: SalesIntelligenceRoot,
        configId: "SalesIntelligenceHome",
        configIdDeps: ["SalesWorkspace", "WebAnalysis", "LeadGenerationReport"],
        secondaryBarType: "SalesIntelligenceHome",
        onEnter: onEnterSalesIntelligenceRoot,
    },

    [HOME_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: "?{subscriptionCancelType:signalsDigest}&openImpactReport",
        // @ts-ignore FIXME: strict migration - Type 'unknown' is not assignable to type 'ComponentType<any>' — redux compose() return type collapses to unknown beyond ~4 HOCs
        component: HomePageContainer,
        configId: "SalesWorkspace",
        configIdDeps: ["WebAnalysis", "MobileWeb", "MobileWebWorldwide"],
        trackingId: {
            section: "SI_home",
            subSection: "SI_home",
            subSubSection: "SI_home",
        },
        pageTitle: "si.pages.home.document_title",
        isPrimarySidebarAlwaysOpen: true,
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceHome" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
        params: homePageParams,
    },

    [MY_LISTS_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MY_LISTS_PAGE_ROUTE_URL,
        component: MyListsPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "target accounts",
            subSection: "accounts home",
            subSubSection: "accounts main",
        },
        pageTitle: "si.pages.my_lists.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
        params: {
            openAddList: { dynamic: true },
        },
    },

    [CRM_LIST_MAIN_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: CRM_LIST_NEW_PAGE_ROUTE_URL,
        component: CrmListsPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "target accounts",
            subSection: "accounts home",
            subSubSection: "accounts main",
        },
        pageTitle: "si.pages.crm_lists.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },
    [SUPPRESSION_LIST_ABSTRACT_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        abstract: true,
        packageName: "salesIntelligence" as Solutions2Package,
        url: MANAGEMENT_ROOT_URL,
        component: SalesIntelligenceContainer,
        configId: "SalesWorkspace",
        secondaryBarType: "None" as SecondaryBarType,
    },

    [MANAGEMENT_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_LIST_ROUTE_URL,
        component: ManagementPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "integrations management",
        },
        pageTitle: "si.pages.suppression_list.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceSuppressionList" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },

    [MANAGEMENT_CRM_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_CRM_URL,
        configId: "SalesWorkspace",
        pageTitle: "si.pages.management.crms-and-connectors",
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceSuppressionList" as SecondaryBarType,
        redirectTo: redirectPipe(
            crmManagementTransitionHook,
            fallback({
                salesIntelligence: MANAGEMENT_ROUTE,
            }),
        ),
    },
    [MANAGEMENT_CRM_INTEGRATIONS_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_CRM_INTEGRATIONS_URL,
        component: ManageCRMsPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "crm management",
        },
        pageTitle: "si.pages.management.crms-and-connectors",
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceSuppressionList" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: MANAGEMENT_ROUTE,
            }),
        ),
    },
    [MANAGEMENT_APIS_AND_WEBHOOKS_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_APIS_AND_WEBHOOKS_URL,
        component: APIsAndWebhooksPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "APIs and webhooks",
        },
        pageTitle: "si.pages.management.api-and-webhooks",
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceSuppressionList" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: MANAGEMENT_ROUTE,
            }),
        ),
    },
    [MANAGEMENT_GATEWAY]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_GATEWAY_URL,
        component: GatewayPageContainer,
        configId: "SalesWorkspace",
        trackingId: createResolveGatewayPageTrackingId({
            section: "SI_admin",
            subSection: "Gateway page",
        }),
        pageTitle: resolveGatewayPageTitle,
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceSuppressionList" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: MANAGEMENT_ROUTE,
            }),
        ),
    },

    [MANAGEMENT_MCP]: {
        parent: SI_ROOT_ROUTE,
        url: MANAGEMENT_MCP_URL,
        component: MCPServersPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "SI_admin",
            subSection: "integrations",
            subSubSection: "MCP servers",
        },
        pageTitle: "si.pages.management.mcp-servers",
        isSecondaryBarOpen: false,
        secondaryBarType: "SalesIntelligenceSuppressionList" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: MANAGEMENT_ROUTE,
            }),
        ),
    },

    [ENRICHMENT_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: ENRICHMENT_PAGE_ROUTE_URL,
        component: EnrichmentPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "Tools & Management",
            subSection: "Enrichment",
            subSubSection: "CSV enrichment",
        },
        pageTitle: "si.pages.enrichment.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },
    [SCORE_SETTINGS_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: SCORE_SETTINGS_PAGE_ROUTE_URL,
        component: ScoringSettingsPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "Tools & Management",
            subSection: "Score Settings",
            subSubSection: "Lead Scoring criteria settings",
        },
        pageTitle: "si.pages.lead_scoring.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },
    [DOWNLOAD_EXTENSION_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: DOWNLOAD_EXTENSION_PAGE_ROUTE_URL,
        component: DownloadExtensionPageContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "target accounts",
            subSection: "accounts home",
            subSubSection: "accounts main",
        },
        pageTitle: "si.pages.download_extension.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },
    [CONTACTS_WATERFALL_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: CONTACTS_WATERFALL_PAGE_ROUTE_URL,
        component: ContactsWaterfallPage,
        configId: "SalesWorkspace",
        trackingId: {
            section: "Tools & Management",
            subSection: "Contacts Waterfall",
            subSubSection: "Contacts Waterfall",
        },
        pageTitle: "si.pages.contacts_waterfall.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            () => {
                if (!isTouchUserAccount()) {
                    return HOME_PAGE_ROUTE;
                }
            },
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },
    [PREMIUM_FEATURES_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: PREMIUM_FEATURES_PAGE_ROUTE_URL,
        component: PremiumFeaturesContainer,
        configId: "SalesWorkspace",
        trackingId: {
            section: "SI Premium features",
            subSection: "SI Premium features",
            subSubSection: "SI Premium features",
        },
        pageTitle: "si.pages.premium_features.document_title",
        isSecondaryBarOpen: false,
        secondaryBarType: "None" as SecondaryBarType,
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "salesIntelligence-home",
            }),
        ),
    },
};

const salesIntelligenceAppsOnlyConfig: RouterStateConfig = {
    [SI_ROOT_ROUTE]: {
        parent: "sw",
        packageName: "salesIntelligence",
        url: SI_ROOT_ROUTE_URL,
        component: SalesIntelligenceRoot,
        configId: "SalesIntelligenceHome",
        configIdDeps: ["SalesWorkspace", "WebAnalysis", "LeadGenerationReport"],
        secondaryBarType: "SalesIntelligenceHome",
        redirectTo: FIND_APPS_SEARCH_PAGE_ROUTE,
    },
    [HOME_PAGE_ROUTE]: {
        parent: SI_ROOT_ROUTE,
        url: "?{subscriptionCancelType:signalsDigest}",
        configId: "SalesWorkspace",
        configIdDeps: ["WebAnalysis", "MobileWeb", "MobileWebWorldwide"],
        trackingId: {
            section: "SI_home",
            subSection: "SI_home",
            subSubSection: "SI_home",
        },
        pageTitle: "si.pages.home.document_title",
        redirectTo: FIND_APPS_SEARCH_PAGE_ROUTE,
    },
    ...findAppsStateConfig,
};

const salesIntelligenceFullConfig: RouterStateConfig = {
    ...salesIntelligenceMain,
    ...COMPANY_ANALYSIS_CONFIG,
    ...OPPORTUNITY_LIST_STATE_CONFIG,
    ...ADVANCED_SEARCH_STATE_CONFIG,
    ...suppressionListConfig,
    ...sfConvertConfig,
    ...salesforceConfig,
    ...hubspotConfig,
    ...dynamicsConfig,
    ...extensionManageConfig,
    ...teamsManageConfig,
    ...playbooksConfig,
    ...employeeProfilesConfig,
    ...signalsManagementConfig,
    ...findAppsStateConfig,
    ...aiAgentStateConfig,
};

export const salesIntelligenceConfig = salesSettingsHelper.isAppsOnly
    ? salesIntelligenceAppsOnlyConfig
    : salesIntelligenceFullConfig;
